import React from 'react';
import UserIcon from 'components/icon/user-icon';
import { useUserContext, useModalContext, LOGIN_REGISTRATION_MODAL } from 'contexts';
import { DesktopNavigation } from '@core/ui/navigation';
import type { NavigationButton, MenuItem, NavigationModuleBehavior, NavigationStyleOverrides } from '@core/ui/navigation';
import styles from './UserMenu.module.scss';

// Type definitions for user menu items and props
interface UserLink {
  id: string;
  label: string;
  link?: string;
  isEmber?: boolean;
  signOut?: boolean;
}

interface UserMenuProps {
  userLinks: UserLink[];
  loginTestId?: string;
  loggedInTestId?: string;
  styleOverrides?: NavigationStyleOverrides;
  additionalComponents?: React.ReactNode;
}

/**
 * UserMenu Component
 * Renders a navigation menu that adapts based on user authentication state
 * Shows login button for unauthenticated users and user menu for authenticated users
 */
const UserMenu = ({ 
  userLinks,
  loginTestId,
  loggedInTestId,
  styleOverrides = {},
  additionalComponents,
}: UserMenuProps) => {
  // Hooks for user authentication and modal management
  const { isAuthenticated, user, isCrawler, signOut } = useUserContext();
  const { openModal } = useModalContext();

  const authText = user ? user.firstName : 'Login';

  // Transform user links into navigation items
  const navItems = React.useMemo((): MenuItem[] => 
    userLinks.map(link => {
      // Handle sign out items specially
      if (link.label === 'Sign Out' || link.signOut) {
        return {
          id: link.id,
          type: 'action',
          label: link.label,
          metadata: {
            handler: 'signOut',
          },
        };
      }
      
      // Regular navigation items
      return {
        id: link.id,
        type: 'link',
        label: link.label,
        href: link.link,
        external: link.isEmber,
      };
    }), [userLinks]);

  // Handle navigation actions (e.g., sign out)
  const handleAction = (handlerName: string) => {
    switch (handlerName) {
      case 'signOut':
        signOut();
        break;
      default:
        console.warn(`Unknown action: ${handlerName}`);
    }
  };

  // Common navigation behavior configuration
  const behavior: NavigationModuleBehavior = {
    dropdownTrigger: 'hover',
    closeOnOutsideClick: true,
    enableSubmenus: false,
    mobileExpandInline: false,
    expandAllSubmenus: isCrawler,
  };

  // Render login button for unauthenticated users
  if (!isAuthenticated) {
    const loginItems: NavigationButton[] = [
      {
        id: 'login',
        buttonVariant: 'link',
        label: 'Login',
        href: '#',
        leftSection: <div className={styles.userIconWrapper}><UserIcon /></div>,
        metadata: {
          handler: 'login',
        },
      },
    ];

    const handleLoginAction = (handlerName: string) => {
      if (handlerName === 'login') {
        openModal(LOGIN_REGISTRATION_MODAL);
      }
    };

    return (
      <div data-testid={loginTestId} className={styles.loginContainer}>
        <DesktopNavigation
          items={loginItems}
          onAction={handleLoginAction}
          styleOverrides={styleOverrides}
          behavior={behavior}
        />
      </div>
    );
  }

  // Configure dropdown for authenticated users
  const dropdownItems: NavigationButton[] = [
    {
      id: 'user-dropdown',
      buttonVariant: 'dropdown',
      label: authText,
      items: navItems,
    },
  ];

  // Render authenticated user menu
  return (
    <>
      {additionalComponents}
      
      <div data-testid={loggedInTestId}>
        <DesktopNavigation
          items={dropdownItems}
          behavior={behavior}
          showChevron={true}
          isCrawler={isCrawler}
          styleOverrides={styleOverrides}
          onAction={handleAction}
        />
      </div>
    </>
  );
};

export default UserMenu;